import React, { useCallback } from 'react';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import { ArrowLeft, Plus, X, Loader2, GripVertical } from 'lucide-react';
import { LayoutGroup, motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useOpenCases, type CaseTab } from '../contexts/OpenCasesContext';
import { useMutation, useQueryClient } from 'react-query';
import { caseApi } from '../services/api';
import { EditableTitle } from './EditableTitle';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { RedactionBlur } from './redaction/RedactionBlur';
import { UserAvatarMenu } from './UserAvatarMenu';

const springTransition = { type: 'spring' as const, stiffness: 400, damping: 30, mass: 0.8 };

// ---------- Single sortable tab ----------
const SortableTab: React.FC<{
  tab: CaseTab;
  isActive: boolean;
  onNavigate: (caseId: string) => void;
  onClose: (e: React.MouseEvent, caseId: string) => void;
  caseNameHasPii?: boolean;
  onUpdateName?: (newName: string) => Promise<void>;
  autoFocusTitle?: boolean;
  isOnlyTab?: boolean;
}> = ({ tab, isActive, onNavigate, onClose, caseNameHasPii, onUpdateName, autoFocusTitle, isOnlyTab }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.caseId });

  const dragStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : isActive ? 10 : undefined,
  };

  if (isActive) {
    return (
      <motion.div
        ref={setNodeRef}
        style={dragStyle}
        layoutId={tab.caseId}
        layout="position"
        transition={springTransition}
        className="flex items-center gap-1 px-3.5 pt-2 pb-1.5 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 border-b-white dark:border-b-medical-800 rounded-t-lg -mb-px relative flex-shrink min-w-[100px]"
      >
        {/* Drag handle */}
        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-medical-300 dark:text-medical-600 hover:text-medical-500 dark:hover:text-medical-400 flex-shrink-0 -ml-1">
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <RedactionBlur forceBlur={caseNameHasPii}>
          <EditableTitle
            title={tab.name}
            onSave={onUpdateName!}
            className="text-sm font-semibold text-medical-900 dark:text-medical-100"
            placeholder="Enter case name..."
            autoFocus={autoFocusTitle}
          />
        </RedactionBlur>
      </motion.div>
    );
  }

  return (
    <motion.button
      ref={setNodeRef}
      style={dragStyle}
      layoutId={tab.caseId}
      layout="position"
      transition={springTransition}
      onClick={() => onNavigate(tab.caseId)}
      className="group/case flex items-center gap-1 px-2.5 pt-1.5 pb-1.5 -mb-px rounded-t-lg text-xs font-medium text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 bg-medical-100 dark:bg-medical-800/50 border border-medical-200 dark:border-medical-700 border-b-medical-200 dark:border-b-medical-700 hover:bg-medical-50 dark:hover:bg-medical-800 transition-colors flex-shrink-0 w-[130px]"
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-medical-300 dark:text-medical-600 hover:text-medical-500 dark:hover:text-medical-400 flex-shrink-0 opacity-0 group-hover/case:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3 w-3" />
      </span>
      {(tab.status === 'ANALYSING' || tab.status === 'PROCESSING') && (
        <div className="relative w-4 h-4 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36" shapeRendering="geometricPrecision">
            <defs>
              <mask id={`tabSeg-${tab.caseId}`}>
                <circle cx="18" cy="18" r="14" fill="none" stroke="white" strokeWidth="5" strokeDasharray="3 1" />
              </mask>
            </defs>
            <g mask={`url(#tabSeg-${tab.caseId})`}>
              <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-medical-300 dark:text-medical-600" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" strokeWidth="3.5"
                strokeDasharray={`${tab.progress ?? 0} 100`}
                className="text-cyan-500 transition-all duration-700 ease-out"
              />
            </g>
          </svg>
        </div>
      )}
      <span className="truncate">{tab.name}</span>
      <span
        onClick={(e) => onClose(e, tab.caseId)}
        className="p-0.5 rounded hover:bg-medical-300 dark:hover:bg-medical-600 opacity-0 group-hover/case:opacity-60 hover:!opacity-100 transition-opacity flex-shrink-0"
      >
        <X className="h-3 w-3" />
      </span>
    </motion.button>
  );
};

// ---------- Main tab bar (lives in Layout, persists across navigations) ----------
export const CaseTabBar: React.FC = () => {
  const match = useMatch('/cases/:caseId/*');
  const activeCaseId = match?.params.caseId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openCases, closeCase, reorderCases, openCase } = useOpenCases();
  const { isEnabled: redactionEnabled, structuredPii } = useRedactionMode();

  const visibleTabs = openCases;
  const tabIds = visibleTabs.map(t => t.caseId);

  // Check if active case name contains PII
  const activeTab = activeCaseId ? openCases.find(t => t.caseId === activeCaseId) : null;
  const caseNameHasPii = redactionEnabled && !!activeTab?.name && [...structuredPii].some(pii =>
    activeTab.name.toLowerCase().includes(pii.toLowerCase())
  );

  // Rename mutation — calls API directly so tab bar is self-contained
  const updateCaseMutation = useMutation(
    ({ id, name }: { id: string; name: string }) => caseApi.updateCase(id, { name }),
    {
      onSuccess: (_data, { id }) => {
        queryClient.invalidateQueries(['case', id]);
        queryClient.invalidateQueries('cases');
      }
    }
  );

  // New case creation
  const nextUntitledName = useCallback(() => {
    const existing = openCases.filter(t => /^Untitled Case( \d+)?$/.test(t.name));
    if (existing.length === 0) return 'Untitled Case';
    const nums = existing.map(t => {
      const m = t.name.match(/^Untitled Case (\d+)$/);
      return m ? parseInt(m[1], 10) : 1;
    });
    return `Untitled Case ${Math.max(...nums) + 1}`;
  }, [openCases]);

  const createCaseMutation = useMutation(
    (name: string) => caseApi.createCase({ name }),
    {
      onSuccess: (data, name) => {
        openCase(data.caseId, name);
        navigate(`/cases/${data.caseId}/records`);
      },
    }
  );

  const handleCloseCaseTab = useCallback((e: React.MouseEvent, tabCaseId: string) => {
    e.stopPropagation();
    e.preventDefault();
    closeCase(tabCaseId);
    // If closing the active tab, navigate to nearest sibling or case list
    if (tabCaseId === activeCaseId) {
      const idx = openCases.findIndex(t => t.caseId === tabCaseId);
      const sibling = openCases[idx + 1] || openCases[idx - 1];
      navigate(sibling ? `/cases/${sibling.caseId}` : '/cases');
    }
  }, [closeCase, activeCaseId, openCases, navigate]);

  // dnd-kit sensors — 8px drag distance to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const allIds = openCases.map(t => t.caseId);
    const oldIndex = allIds.indexOf(active.id as string);
    const newIndex = allIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    reorderCases(arrayMove(allIds, oldIndex, newIndex));
  }, [openCases, reorderCases]);

  return (
    <div className="bg-medical-50 dark:bg-medical-900 px-4 pt-2">
      <div className="flex items-end gap-0 min-w-0 border-b border-medical-200 dark:border-medical-700">
        {/* Back */}
        <Link
          to="/cases"
          className="p-1 mb-1.5 mx-1 hover:bg-medical-200/60 dark:hover:bg-medical-700 rounded-md transition-colors flex-shrink-0"
        >
          <ArrowLeft className="h-4 w-4 text-medical-500 dark:text-medical-400" />
        </Link>

        {/* All tabs in stable array order, animated with Framer Motion layoutId */}
        <LayoutGroup>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
              <AnimatePresence initial={false}>
                {visibleTabs.map(tab => (
                  <SortableTab
                    key={tab.caseId}
                    tab={tab}
                    isActive={tab.caseId === activeCaseId}
                    onNavigate={(id) => {
                      const stored = openCases.find(t => t.caseId === id)?.lastTab;
                      navigate(stored ? `/cases/${id}/${stored}` : `/cases/${id}`);
                    }}
                    onClose={handleCloseCaseTab}
                    caseNameHasPii={tab.caseId === activeCaseId ? caseNameHasPii : undefined}
                    onUpdateName={tab.caseId === activeCaseId
                      ? async (name) => { await updateCaseMutation.mutateAsync({ id: tab.caseId, name }); }
                      : undefined
                    }
                    autoFocusTitle={tab.caseId === activeCaseId && tab.name === 'Untitled Case'}
                    isOnlyTab={openCases.length === 1}
                  />
                ))}
              </AnimatePresence>
            </SortableContext>
          </DndContext>
        </LayoutGroup>

        {/* New case */}
        <button
          onClick={() => createCaseMutation.mutate(nextUntitledName())}
          disabled={createCaseMutation.isLoading}
          className="flex-shrink-0 p-1.5 mb-1 rounded-md text-medical-400 dark:text-medical-500 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-200/60 dark:hover:bg-medical-700 transition-colors"
          title="New case"
        >
          {createCaseMutation.isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Spacer */}
        <div className="flex-1 min-w-0" />

        {/* User avatar */}
        <div className="flex-shrink-0 mb-1">
          <UserAvatarMenu />
        </div>
      </div>
    </div>
  );
};
